/**
 * Treat — <treat-currency-form>: a currency picker dressed as the language picker, driving BUCKS.
 *
 * BUCKS renders its own widget into the hidden engine box (layout/theme.liquid). This element
 * lists the currencies chosen in the app, opens and closes like Sense's LocalizationForm, and on a
 * choice clicks the matching option inside the BUCKS widget, which converts the prices. Labels are
 * kept in sync with the widget, so automatic switching by location shows up here too.
 */
(() => {
  if (customElements.get('treat-currency-form')) return;

  // The app's widget lives in the hidden engine box when BUCKS is on "Custom position", or in its
  // own floating box when it is on "Floating" (that box is hidden in treat-foundation.css).
  const ENGINES = ['.treat-bucks-engine', '.buckscc-floating-box'];
  const inEngine = (selector) => {
    for (const root of ENGINES) {
      const found = document.querySelector(`${root} ${selector}`);
      if (found) return found;
    }
    return null;
  };
  const engineRoot = () => {
    const label = inEngine('.bucks-selected');
    return label ? label.closest(ENGINES.join(',')) : null;
  };
  const forms = new Set();
  let readyCodes = null;

  const readCurrencies = () => {
    const raw = window.bucksCC && window.bucksCC.metaConfig && window.bucksCC.metaConfig.selectedCurrencies;
    if (!raw) return [];
    try {
      const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return list.map((entry) => Object.keys(entry)[0]).filter(Boolean);
    } catch (error) {
      return [];
    }
  };

  const currentCurrency = () => {
    const label = inEngine('.bucks-selected');
    return label ? label.textContent.trim() : '';
  };

  const syncAll = () => {
    const code = currentCurrency();
    if (code) forms.forEach((form) => form.setCurrent(code));
  };

  class TreatCurrencyForm extends HTMLElement {
    connectedCallback() {
      forms.add(this);
      // Sense moves header elements around (sticky header, drawer); bind the listeners once.
      if (this.initialised) return;
      this.initialised = true;
      this.button = this.querySelector('button.localization-form__select');
      this.panel = this.querySelector('.disclosure__list-wrapper');
      this.list = this.querySelector('ul');
      this.label = this.querySelector('[data-currency-label]');
      this.template = this.querySelector('template');
      this.header = document.querySelector('.header-wrapper');

      this.button.addEventListener('click', () => this.toggle());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hide();
      });
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape' && this.button.getAttribute('aria-expanded') === 'true') {
          this.hide();
          event.stopPropagation();
          this.button.focus();
        }
      });
      this.addEventListener('keydown', (event) => this.onKeyDown(event));
      if (readyCodes) {
        this.build(readyCodes);
        syncAll();
      } else {
        waitForApp();
      }
    }

    disconnectedCallback() {
      forms.delete(this);
    }

    build(codes) {
      if (this.built || codes.length === 0) return;
      this.built = true;
      codes.forEach((code) => {
        const item = this.template.content.firstElementChild.cloneNode(true);
        const link = item.querySelector('a');
        link.dataset.value = code;
        item.querySelector('[data-currency-code]').textContent = code;
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.choose(code);
        });
        this.list.appendChild(item);
      });
      this.removeAttribute('hidden');
    }

    setCurrent(code) {
      this.label.textContent = code;
      this.list.querySelectorAll('a').forEach((link) => {
        const isCurrent = link.dataset.value === code;
        link.toggleAttribute('aria-current', isCurrent);
        link.firstElementChild.classList.toggle('visibility-hidden', !isCurrent);
      });
    }

    choose(code) {
      const option = inEngine(`.buckscc-select-options li.converterTriggers:not(.geoCurrency)[rel="${code}"]`);
      if (option) option.click();
      forms.forEach((form) => form.setCurrent(code));
      this.hide();
      this.button.focus();
    }

    toggle() {
      const open = this.button.getAttribute('aria-expanded') !== 'true';
      this.button.setAttribute('aria-expanded', String(open));
      this.panel.toggleAttribute('hidden', !open);
      if (this.header && this.closest('.header')) this.header.preventHide = open;
    }

    hide() {
      this.button.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('hidden', '');
      if (this.header && this.closest('.header')) this.header.preventHide = false;
    }

    onKeyDown(event) {
      if (event.code !== 'ArrowUp' && event.code !== 'ArrowDown') return;
      const links = Array.from(this.list.querySelectorAll('a'));
      if (links.length === 0) return;
      event.preventDefault();
      const index = links.indexOf(document.activeElement);
      const next =
        event.code === 'ArrowDown'
          ? links[(index + 1) % links.length]
          : links[(index - 1 + links.length) % links.length];
      next.focus();
    }
  }

  let waiting = false;
  const waitForApp = () => {
    if (waiting) return;
    waiting = true;
    let tries = 0;
    const tick = () => {
      const codes = readCurrencies();
      const engineReady = engineRoot();
      if (codes.length && engineReady) {
        readyCodes = codes;
        forms.forEach((form) => form.build(codes));
        syncAll();
        new MutationObserver(syncAll).observe(engineReady, {
          subtree: true,
          childList: true,
          characterData: true,
        });
        return;
      }
      if (++tries < 60) window.setTimeout(tick, 250);
    };
    tick();
  };

  customElements.define('treat-currency-form', TreatCurrencyForm);
})();
